// The socket relays geometry; each observer intersects its own perceived scene.

import type { WSMessage } from './types';
import { ft3dTargetingPreviewClientId, isOwnTargetingPreview, targetingPreviewStreamKey } from '../shared/ft3d-targeting-preview-client';
import { ws } from './state';
import { actorEncounterFor, actorRowFor } from './movement-truth';
import { definitionFor } from './held-definition';
import { aoePreviewVisibleToViewer } from './aoe-preview-visibility';

Object.assign(window, { ft3dTargetingPreviewClientId });

type PreviewMessage = Extract<WSMessage, { type: 'ft3d_aoe_placement_preview' }>;
type ClearMessage = Extract<WSMessage, { type: 'ft3d_aoe_placement_preview_clear' }>;
type AoeMessage = PreviewMessage | ClearMessage;

const latestSequenceByUser = new Map<string, number>();
const previewSockets = new WeakSet<WebSocket>();
let reportedReason = '';

export function consumeAoePreviewTransportControl(socket: WebSocket, message: { type?: string; aoePreview?: boolean; reason?: string }): boolean {
    if (message.type === 'transport_ready') {
        if (message.aoePreview === true) previewSockets.add(socket);
        return false;
    }
    if (message.type !== 'transport_aoe_preview') return false;
    reportPreviewUnavailable(message.reason ?? 'aoe-preview:transport-unavailable');
    return true;
}

function reportPreviewUnavailable(reason: string): void {
    if (reportedReason === reason) return;
    reportedReason = reason;
    // Observer geometry is optional. The local placement and its cast are still available.
    ftDebugLog('aoe-preview', { reason, capability: 'observer AOE preview', localPlacementAvailable: true });
}

window.addEventListener('message', event => {
    if (event.origin !== window.location.origin || event.data?.type !== 'ft3d-campaign-aoe-preview-send-v1') return;
    if (![...document.querySelectorAll<HTMLIFrameElement>('[data-ft3d-encounter-frame]')]
        .some(frame => frame.contentWindow === event.source)) return;
    const payload = event.data.payload as Record<string, unknown> | undefined;
    if (!payload || !['ft3d_aoe_placement_preview', 'ft3d_aoe_placement_preview_clear'].includes(String(payload.type))) return;
    if (payload.type === 'ft3d_aoe_placement_preview_clear') reportedReason = '';
    if (!ws || ws.readyState !== WebSocket.OPEN || !previewSockets.has(ws)) {
        if (payload.type !== 'ft3d_aoe_placement_preview_clear') reportPreviewUnavailable('aoe-preview:transport-unavailable');
        return;
    }
    // A congested socket drops pointer samples; the next heartbeat carries the current geometry.
    if (ws.bufferedAmount > 16_384 && payload.type !== 'ft3d_aoe_placement_preview_clear') return;
    ws.send(JSON.stringify({ ...payload, from_client_id: ft3dTargetingPreviewClientId() }));
});

function isOwnMessage(data: AoeMessage): boolean {
    const context = getSidelogContext();
    return Boolean(context && isOwnTargetingPreview(data, context.user_id));
}

function shouldDispatchFt3dAoePlacement(data: AoeMessage): boolean {
    if (data.schema !== 'ft.aoe-placement.remote-preview.v1' || Number(data.version) !== 1
        || !checkIfValidUUID(data.encounter_id) || !checkIfValidUUID(data.from_user_id)
        || !checkIfValidUUID(data.caster_id) || isOwnMessage(data)) return false;
    const sequence = Number(data.sequence);
    if (!Number.isSafeInteger(sequence) || sequence < 1) return false;
    const userId = targetingPreviewStreamKey(data);
    const previous = latestSequenceByUser.get(userId) ?? 0;
    if (sequence <= previous) return false;
    latestSequenceByUser.set(userId, sequence);
    if (latestSequenceByUser.size > 256) {
        const oldest = latestSequenceByUser.keys().next().value as string | undefined;
        if (oldest) latestSequenceByUser.delete(oldest);
    }
    if (data.type === 'ft3d_aoe_placement_preview_clear') return true;
    const encounterId = data.encounter_id.toUpperCase();
    const caster = actorRowFor(encounterId, data.caster_id);
    const ability = data.ability_id ? definitionFor(caster, actorEncounterFor(encounterId), data.ability_id) : null;
    return aoePreviewVisibleToViewer(data, caster, ability, { isGM: window.FateTide?.user?.isGM === true });
}

function handleFt3dAoePlacementPreview(data: PreviewMessage): void {
    if (!shouldDispatchFt3dAoePlacement(data)) return;
    window.dispatchEvent(new CustomEvent('ft:remote-aoe-placement-preview', { detail: data }));
}

function handleFt3dAoePlacementPreviewClear(data: ClearMessage): void {
    if (!shouldDispatchFt3dAoePlacement(data)) return;
    window.dispatchEvent(new CustomEvent('ft:remote-aoe-placement-preview-clear', { detail: data }));
}

window.handleFt3dAoePlacementPreview = handleFt3dAoePlacementPreview;
window.handleFt3dAoePlacementPreviewClear = handleFt3dAoePlacementPreviewClear;

export {
    handleFt3dAoePlacementPreview,
    handleFt3dAoePlacementPreviewClear,
    shouldDispatchFt3dAoePlacement,
};
